import { useCallback, useEffect, useMemo, useState } from "react";
import { TbArrowsExchange } from "react-icons/tb";
import { CurrencyIcon } from "../../../../Components/Atoms/CurrencyIcon";
import { useToast } from "../../../../Hooks/toast";
import { useContaInternacionalBalances } from "../../../../Hooks/useContaInternacionalBalances";
import { useProgressAlert } from "../../../../Hooks/useProgressAlert";
import {
  createIbFxConversion,
  createIbFxQuote,
  formatBaasErrorMessage,
} from "../../../../Services/ibContaInternacionalOpsApi";
import { ContaInternacionalConverterSummary } from "./ContaInternacionalConverterSummary";
import { CurrencySelect } from "./CurrencySelect";
import {
  CURRENCIES,
  DEFAULT_CURRENCY_ID,
  formatCurrencyAmount,
  getCurrency,
} from "../constants";

export const ContaInternacionalMovimentarContent = ({
  isActive = true,
  currencyId,
  hideActionButton = false,
  onRegisterActions,
  onClose,
}) => {
  const toast = useToast();
  const progress = useProgressAlert();
  const { allCurrencies } = useContaInternacionalBalances();

  const currencyOptions = useMemo(
    () => (allCurrencies?.length ? allCurrencies : CURRENCIES),
    [allCurrencies],
  );

  const defaultId = currencyOptions[0]?.id ?? DEFAULT_CURRENCY_ID;
  const [fromId, setFromId] = useState(currencyId ?? defaultId);

  const resolveCurrency = useCallback(
    (id) =>
      currencyOptions.find((row) => row.id === id) || getCurrency(id) || null,
    [currencyOptions],
  );

  const currency = resolveCurrency(fromId);
  const destinationOptions = useMemo(
    () => currencyOptions.filter((c) => c.id !== currency?.id),
    [currencyOptions, currency],
  );
  const [toId, setToId] = useState(() => destinationOptions[0]?.id ?? "");
  const [amount, setAmount] = useState("");

  useEffect(() => {
    if (!isActive) return;

    const nextFromId = currencyId ?? defaultId;
    const nextCurrency = resolveCurrency(nextFromId);
    const nextDestinations = currencyOptions.filter((c) => c.id !== nextCurrency?.id);

    setFromId(nextFromId);
    setToId(nextDestinations[0]?.id ?? "");
    setAmount("");
  }, [isActive, currencyId, defaultId, currencyOptions, resolveCurrency]);

  useEffect(() => {
    if (!destinationOptions.some((c) => c.id === toId)) {
      setToId(destinationOptions[0]?.id ?? "");
    }
  }, [destinationOptions, toId]);

  const toCurrency = resolveCurrency(toId);
  const numericAmount = parseFloat(amount.replace(",", ".")) || 0;
  const balanceAfter = Math.max(0, Number(currency?.balance || 0) - numericAmount);

  const handleSwapCurrencies = () => {
    if (currencyId) return;
    setFromId(toId);
    setToId(fromId);
  };

  const handleSubmit = useCallback(() => {
    if (!currency) return;

    if (!toCurrency) {
      toast.addToast({
        type: "error",
        title: "Conta destino",
        description: "Selecione a conta de destino.",
      });
      return;
    }
    if (!numericAmount || numericAmount <= 0) {
      toast.addToast({
        type: "error",
        title: "Valor inválido",
        description: "Informe um valor maior que zero.",
      });
      return;
    }
    if (numericAmount > Number(currency.balance || 0)) {
      toast.addToast({
        type: "error",
        title: "Saldo insuficiente",
        description: `Saldo disponível: ${formatCurrencyAmount(currency.balance, currency.id)}`,
      });
      return;
    }

    const movementPreview = `${currency.code} → ${toCurrency.code} — ${formatCurrencyAmount(numericAmount, currency.id)}`;

    progress
      .runWithProgress(
        async () => {
          try {
            const quote = await createIbFxQuote({
              fromCurrency: currency.code,
              toCurrency: toCurrency.code,
              amountMajor: numericAmount,
            });
            await createIbFxConversion({ quoteId: quote.quoteId });
            return {
              success: true,
              message: "Movimentação concluída",
              description: `${movementPreview} → ${formatCurrencyAmount(quote.toAmountMajor, toCurrency.id)}`,
            };
          } catch (error) {
            return {
              success: false,
              message: "Falha na movimentação",
              description: formatBaasErrorMessage(error),
            };
          }
        },
        {
          loading: "Processando movimentação...",
          loadingDescription: movementPreview,
          success: "Movimentação concluída",
          error: "Falha na movimentação",
        },
        { beforeProgress: onClose },
      )
      .catch(() => {});
  }, [currency, numericAmount, onClose, progress, toast, toCurrency]);

  useEffect(() => {
    onRegisterActions?.({
      submit: handleSubmit,
      label: "Movimentar para outra conta",
    });
  }, [handleSubmit, onRegisterActions]);

  if (!currency) return null;

  return (
    <div className="grid grid-cols-1 gap-3 lg:grid-cols-2 lg:items-start">
      <section className="space-y-3">
        {!currencyId ? (
          <div>
            <CurrencySelect
              id="move-origin-currency"
              label="Conta de origem"
              value={fromId}
              onChange={setFromId}
              options={currencyOptions}
              exclude={toId}
            />
            <p className="mt-1.5 text-xs text-gray-400">
              Disponível: {formatCurrencyAmount(currency.balance, currency.id)}
            </p>
          </div>
        ) : (
          <div>
            <p className="mb-2 text-xs font-medium text-gray-500">Conta de origem</p>
            <div className="flex items-center gap-3 rounded-xl border border-gray-100 px-3 py-2.5">
              <CurrencyIcon asset={currency.id} />
              <div>
                <p className="text-sm font-bold text-brand-dark">{currency.label}</p>
                <p className="text-xs text-gray-500">
                  Disponível: {formatCurrencyAmount(currency.balance, currency.id)}
                </p>
              </div>
            </div>
          </div>
        )}

        {!currencyId && (
          <div className="flex justify-center py-0.5">
            <button
              type="button"
              onClick={handleSwapCurrencies}
              className="btn-outline-icon"
              aria-label="Inverter moedas"
            >
              <TbArrowsExchange className="h-4 w-4" />
            </button>
          </div>
        )}

        <CurrencySelect
          id="move-destination-currency"
          label="Conta de destino"
          value={toId}
          onChange={setToId}
          options={destinationOptions}
          exclude={fromId}
        />

        <div>
          <label htmlFor="move-amount" className="mb-1.5 block text-xs font-medium text-gray-500">
            Valor a movimentar
          </label>
          <div className="relative">
            <input
              id="move-amount"
              type="text"
              inputMode="decimal"
              placeholder="0,00"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="valut-input h-10 w-full rounded-xl pr-16"
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-semibold text-gray-400">
              {currency.code}
            </span>
          </div>
        </div>
      </section>

      <section className="space-y-2 lg:sticky lg:top-0">
        <ContaInternacionalConverterSummary fromId={fromId} toId={toId} amount={numericAmount} />
        <div className="rounded-xl border border-gray-100 bg-gray-50/50 px-3 py-2.5 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-500">Saldo após (origem)</span>
            <span className="font-semibold text-brand-dark">
              {formatCurrencyAmount(balanceAfter, currency.id)}
            </span>
          </div>
        </div>
        {!hideActionButton && (
          <button type="button" className="btn-primary mt-3 w-full" onClick={handleSubmit}>
            Movimentar para outra conta
          </button>
        )}
      </section>
    </div>
  );
};
